import { createBrowserRouter } from "react-router-dom"
import AuthenticatedRoute from "../components/AuthenticatedRoute"
import LayoutComponent from "../components/LayoutComponent"
import ErrorScreen from "../core-components/theme/components/ErrorScreen"
import withLazyLoading from "../hoc/withLazyLoading"
import { createCrumbHandle } from "../utilities/auth"
import dashboardRoutes from "./modulesRoutes/dashboardRoutes"
import formulaRoutes from "./modulesRoutes/formulaRoutes"
import inventoryRoutes from "./modulesRoutes/inventoryRoutes"
import miscRoutes from "./modulesRoutes/miscRoutes"
import plantRoutes from "./modulesRoutes/plantRoutes"
import taskRoutes from "./modulesRoutes/taskRoutes"
import userRoutes from "./modulesRoutes/userRoutes"
import visualizationRoutes from "./modulesRoutes/visualizationRoutes"
import workspaceRoutes from "./modulesRoutes/workspaceRoutes"
import manualInputRoutes from "./modulesRoutes/manualInputRoutes"
import dataInputRoutes from "./modulesRoutes/dataInputRoutes"
import Notifications from "../components/Notifications/pages/Notifications"
import { QueryClient } from "@tanstack/react-query"
import fileManagementRoute from "./modulesRoutes/fileManagementRoute"
import batchConfigRoute from "./modulesRoutes/batchConfigRoute"
import setPointConfigurationRoute from "./modulesRoutes/setPointConfigurationRoute"
import userCenterRoutes from "./modulesRoutes/userCenterRoutes"
import closeTheLoopRoutes from "./modulesRoutes/closeTheLoopRoutes"
import accountRoutes from "./modulesRoutes/accountRoutes"
import publicRoutes from "./modulesRoutes/publicRoutes"
import DataLoggerList from "../components/DataLogger/components/DataLoggerList"
import withPostHogTracker from "../composeRouter"
import { bootLoader } from "./bootLoader"
import { CTL_PATTERNS, LEGACY_LOGIN_PATHS, ONBOARDING_ROUTES, PASSCODE_ROUTES } from "../navigation/paths"
import { InAppAlertsRoute } from "../modules/close-the-loop/components/inapp/InAppAlertsRoute"

const Home = withLazyLoading(() => import("../components/Home"))
const Page404 = withLazyLoading(() => import("../pages/Page404"))
const CompanyListing = withLazyLoading(() => import("../pages/company/companyListing"));
const CompanyDetailsListing = withLazyLoading(() => import("../pages/company/companyDetailsListing"))
const SensorListing = withLazyLoading(() => import("../pages/sensor/sensorListing"))
const InsightsTemplateList = withLazyLoading(() => import("../components/Insights/components/InsightsTemplateList"));
const EventTemplateList = withLazyLoading(() => import("../components/Events/components/EventTemplateList"));
const CreateInsightsTemplatePage = withLazyLoading(() => import("../components/Insights/components/AddInsightsTemplate"))
const InsightsListView = withLazyLoading(() => import("../components/Insights/components/InsightsListView"))
const SensorHealthDashboardView = withLazyLoading(() => import("../components/SensorHealthDashboard/components/SensorHealthDashboardView"))
const SensorHealthPlantDetailView = withLazyLoading(() => import("../components/SensorHealthDashboard/components/PlantDetailView"))
const EventList = withLazyLoading(() => import("../components/Events/components/EventList"))
const AuthorizationFailed = withLazyLoading(
    () => import('../core-components/theme/components/DialogBox/AuthorizationFailed')
);
const EmailPage = withLazyLoading(() => import('../pages/EmailPage'));
const DashboardA4PrintPage = withLazyLoading(() => import('../pages/dashboardA4PrintPage'));
const LoginRoute = withLazyLoading(() =>
  import('../modules/passcode-auth/components/LoginRoute').then((m) => ({ default: m.LoginRoute })),
);
const LoginRedirect = withLazyLoading(() =>
  import('../modules/passcode-auth/components/LoginRoute').then((m) => ({ default: m.LoginRedirect })),
);
const IssueLinkLanding = withLazyLoading(() =>
  import('../modules/close-the-loop/components/landing/IssueLinkLandingScreen').then((m) => ({ default: m.IssueLinkLandingScreen })),
);
const IssueLanding = withLazyLoading(() =>
  import('../modules/close-the-loop/components/landing/IssueLandingScreen').then((m) => ({ default: m.IssueLandingScreen })),
);
const OnboardingWizard = withLazyLoading(() =>
  import('../modules/onboarding').then((m) => ({ default: m.OnboardingWizard })),
);
const StartScreen = withLazyLoading(() =>
  import('../modules/onboarding').then((m) => ({ default: m.StartScreen })),
);

const router = (queryClient: QueryClient) => createBrowserRouter([
  {
    path: PASSCODE_ROUTES.login,
    element: withPostHogTracker(<LoginRoute />),
  },
  ...LEGACY_LOGIN_PATHS.map((path) => ({
    path,
    element: <LoginRedirect />,
  })),
  {
    path: CTL_PATTERNS.landing,
    element: <IssueLinkLanding />,
  },
  ...publicRoutes,
  {
    path: "/emailPage/:plantId/:pageId/:days/:startDateUrl/:endDateUrl/:granularity",
    element: withPostHogTracker(<EmailPage />),
  },
  {
    path: "/dashboardA4PrintPage/:plantId/:pageId/:days/:startDateUrl/:endDateUrl/:granularity",
    element: withPostHogTracker(<DashboardA4PrintPage />),
  },
  {
    element: <InAppAlertsRoute />,
    children: [
      {
        path: CTL_PATTERNS.legacyIssue,
        element: withPostHogTracker(<IssueLanding />),
      },
      {
        path: ONBOARDING_ROUTES.start,
        element: withPostHogTracker(<StartScreen />),
      },
      {
        element: <AuthenticatedRoute />,
        errorElement: <ErrorScreen />,
        loader: bootLoader(queryClient),
        children: [
          {
            path: ONBOARDING_ROUTES.wizard,
            element: withPostHogTracker(<OnboardingWizard />),
          },
          {
            path: "/",
            element: <LayoutComponent />,
            errorElement: <ErrorScreen />,
            children: [
              {
                index: true,
                element: withPostHogTracker(<Home />),
              },
              {
                path: "/company-list",
                handle: createCrumbHandle("Companies"),
                children: [
                  {
                    index: true,
                    element: withPostHogTracker(<CompanyListing />),
                  },
                  {
                    path: "company-details/:companyid?",
                    element: withPostHogTracker(<CompanyDetailsListing />),
                    handle: createCrumbHandle("Company Details"),
                  },
                ],
              },
              {
                path: "/sensor-list",
                element: withPostHogTracker(<SensorListing />),
                handle: createCrumbHandle("Sensors"),
              },
              {
                path: "/insightsTemplates",
                handle: createCrumbHandle("Insights Templates"),
                children: [
                  {
                    index: true,
                    element: withPostHogTracker(<InsightsTemplateList />),
                  },
                  {
                    path: "insightsTemplate/:insightsTemplateId?",
                    element: withPostHogTracker(<CreateInsightsTemplatePage />),
                    handle: createCrumbHandle("Create Insights Template"),
                  },
                ],
              },
              {
                path: "/data-logger",
                element: withPostHogTracker(<DataLoggerList />),
                handle: createCrumbHandle("Data Logger"),
              },
              {
                path: "/insightList",
                element: withPostHogTracker(<InsightsListView />),
                handle: createCrumbHandle("Insights"),
              },
              {
                path: "/sensor-health",
                handle: createCrumbHandle("Sensor Health"),
                children: [
                  {
                    index: true,
                    element: withPostHogTracker(<SensorHealthDashboardView />),
                  },
                  {
                    path: "plants/:plantId",
                    element: withPostHogTracker(<SensorHealthPlantDetailView />),
                    handle: createCrumbHandle("Faults"),
                  },
                ],
              },
              {
                path: "/eventTemplates",
                element: withPostHogTracker(<EventTemplateList />),
                handle: createCrumbHandle("Event Templates"),
              },
              {
                path: "/events",
                element: withPostHogTracker(<EventList />),
                handle: createCrumbHandle("Insights"),
              },
              {
                path: "/authorizationFailed",
                element: withPostHogTracker(<AuthorizationFailed />),
              },
              {
                path: "/notifications",
                element: withPostHogTracker(<Notifications />),
              },
              {
                path: "*",
                element: withPostHogTracker(<Page404 />),
              },
              ...userRoutes(queryClient),
              ...dashboardRoutes,
              ...plantRoutes,
              ...taskRoutes,
              ...formulaRoutes,
              ...workspaceRoutes,
              ...visualizationRoutes(queryClient),
              ...inventoryRoutes,
              ...miscRoutes(queryClient),
              ...manualInputRoutes,
              ...dataInputRoutes,
              ...fileManagementRoute,
              ...batchConfigRoute,
              ...setPointConfigurationRoute,
              ...userCenterRoutes,
              ...closeTheLoopRoutes,
              ...accountRoutes,
            ],
          },
        ],
      },
    ],
  },
]);

export default router;